import { useEffect, useState } from 'react';
import { parsePppMe } from './ppp.js';
/**
 * Per client, the people whose GET /v1/ppp/me answered 404, with the `refreshKey` of that read. A service older than PPP
 * has no such route (nor does a host proxy that passes only the routes it lists), so it is not asked again for them on
 * every panel open: only once `refreshKey` changes (an export shows the service has PPP now) or the page reloads. A read
 * that answers forgets it; any other refusal (401, 503) is not remembered, so the next open asks again.
 */
const routeMissing = new WeakMap();
/** A 404: the client's error (httpError) carries the HTTP status as `status`, and a host's stand-in may too. */
function isNotFound(error) {
    return typeof error === 'object' && error !== null && error.status === 404;
}
/**
 * GET /v1/ppp/me for the gated starter and the due line (ppp.ts). Null until it answers, and for good when the read fails
 * or is refused: a failed read means no starter, never a default of "on". Null, and forgotten, while `enabled` is false.
 * Read again when `refreshKey` changes and each time `onScreen` turns true. A client without the method (a host's own
 * stand-in) reads as null.
 */
export function usePppMe({ client, owner, enabled, onScreen = true, refreshKey }) {
    const [answer, setAnswer] = useState(null);
    // Off (a later /v1/status said pppEnabled false, say): forget the answer in this render, before anything shows it.
    if (!enabled && answer !== null)
        setAnswer(null);
    useEffect(() => {
        if (!enabled || !onScreen || typeof client.pppMe !== 'function' || routeMissing.get(client)?.get(owner) === refreshKey)
            return undefined;
        let cancelled = false;
        const settle = (me) => { if (!cancelled)
            setAnswer({ client, owner, me }); };
        const answered = (value) => {
            routeMissing.get(client)?.delete(owner);
            settle(parsePppMe(value));
        };
        const refused = (error) => {
            if (isNotFound(error)) {
                const missing = routeMissing.get(client) ?? new Map();
                missing.set(owner, refreshKey);
                routeMissing.set(client, missing);
            }
            settle(null);
        };
        try {
            Promise.resolve(client.pppMe()).then(answered, refused);
        }
        catch (error) {
            refused(error);
        }
        return () => { cancelled = true; };
    }, [client, owner, enabled, onScreen, refreshKey]);
    return enabled && answer && answer.client === client && answer.owner === owner ? answer.me : null;
}
//# sourceMappingURL=usePppMe.js.map